import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Switch,
  Text,
  View,
} from 'react-native';

import { reviewDesk } from '@/design-system';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { ICFProgressAPI } from '@/services/api/icfProgressAPI';
import { trackAuthEvent } from '@/services/analytics';
import { AnalysisStatus, SessionAnalysisResponse } from '@/types/icfProgress';

interface ProgressSessionsPanelProps {
  onProgressChanged: () => Promise<void> | void;
}

export function ProgressSessionsPanel({ onProgressChanged }: ProgressSessionsPanelProps) {
  const { t, language } = useTranslation();
  const { isPhone, isDesktop } = useResponsive();
  const [sessions, setSessions] = useState<SessionAnalysisResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState('');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const loaded: SessionAnalysisResponse[] = [];
      let offset = 0;
      while (true) {
        const page = await ICFProgressAPI.getSessionAnalyses({
          limit: 100,
          offset,
          include_excluded: true,
        });
        loaded.push(...page);
        if (page.length < 100) break;
        offset += page.length;
      }
      setSessions(loaded);
    } catch {
      setError(t('icfProgress.sessions.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const markBusy = (sessionId: number, busy: boolean) => {
    setBusyIds((current) => {
      const next = new Set(current);
      if (busy) next.add(sessionId);
      else next.delete(sessionId);
      return next;
    });
  };

  const toggleSession = async (session: SessionAnalysisResponse, included: boolean) => {
    if (!session.has_progress_tracking) return;
    markBusy(session.session_id, true);
    setError('');
    try {
      await ICFProgressAPI.toggleSessionAnalysis(session.session_id, included);
      setSessions((current) => current.map((item) => item.session_id === session.session_id
        ? { ...item, include_in_analysis: included }
        : item));
      trackAuthEvent('progress_session_toggled', {
        session_id_present: true,
        included,
      });
      ICFProgressAPI.invalidateDashboardCache();
      await onProgressChanged();
    } catch {
      setError(t('icfProgress.sessions.toggleError'));
    } finally {
      markBusy(session.session_id, false);
    }
  };

  return (
    <View testID="progress_sessions_panel" style={styles.panel}>
      <View style={[styles.header, isPhone && styles.headerPhone]}>
        <View style={styles.headerCopy}>
          <Text selectable style={styles.title}>{t('icfProgress.sessions.title')}</Text>
          <Text selectable style={styles.subtitle}>{t('icfProgress.sessions.subtitle')}</Text>
        </View>
      </View>

      {!!error && <Text selectable style={styles.error}>{error}</Text>}
      {loading ? (
        <ActivityIndicator color={reviewDesk.accent} />
      ) : sessions.length === 0 ? (
        <Text selectable style={styles.empty}>{t('icfProgress.sessions.empty')}</Text>
      ) : (
        <View style={styles.list}>
          {sessions.map((session) => {
            const busy = busyIds.has(session.session_id);
            const trackable = Boolean(session.has_progress_tracking);
            const included = trackable && Boolean(session.include_in_analysis);
            const processing = session.analysis_status === AnalysisStatus.PROCESSING
              || session.analysis_status === AnalysisStatus.PENDING;
            return (
              <View
                testID={`progress_session_row_${session.session_id}`}
                key={session.session_id}
                style={[styles.row, isPhone && styles.rowPhone, !trackable && styles.rowMuted]}
              >
                <View style={styles.sessionCopy}>
                  <Text
                    selectable
                    numberOfLines={2}
                    style={[styles.sessionTitle, !trackable && styles.mutedInk]}
                  >
                    {session.session_title || t('icfProgress.sessions.sessionFallback', { id: session.session_id })}
                  </Text>
                  <Text selectable style={styles.sessionMeta}>
                    {session.last_analyzed_at
                      ? new Date(session.last_analyzed_at).toLocaleDateString(language)
                      : t('icfProgress.sessions.notAnalyzed')}
                    {trackable && processing ? ` · ${t('icfProgress.sessions.processing')}` : ''}
                  </Text>
                  {!trackable && (
                    <Text
                      selectable
                      testID={`progress_session_untracked_${session.session_id}`}
                      style={styles.sessionHint}
                    >
                      {t('icfProgress.sessions.notTrackedHint')}
                    </Text>
                  )}
                </View>
                <View style={[styles.toggleGroup, !isDesktop && styles.toggleGroupCompact]}>
                  {!trackable ? (
                    <Text selectable style={[styles.toggleLabel, styles.mutedInk]}>
                      {t('icfProgress.sessions.notTracked')}
                    </Text>
                  ) : (
                    <>
                      <Text selectable style={styles.toggleLabel}>
                        {included ? t('icfProgress.sessions.included') : t('icfProgress.sessions.excluded')}
                      </Text>
                      {busy ? (
                        <ActivityIndicator size="small" color={reviewDesk.accent} />
                      ) : (
                        <Switch
                          testID={`progress_session_toggle_${session.session_id}`}
                          accessibilityRole="switch"
                          accessibilityLabel={t('icfProgress.sessions.toggleLabel', {
                            title: session.session_title || session.session_id,
                          })}
                          accessibilityState={{ checked: included, disabled: processing }}
                          value={included}
                          disabled={processing}
                          onValueChange={(value) => void toggleSession(session, value)}
                          trackColor={{ false: reviewDesk.lineStrong, true: '#AAB8F0' }}
                          thumbColor={included ? reviewDesk.accent : reviewDesk.muted}
                        />
                      )}
                    </>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: { width: '100%', backgroundColor: reviewDesk.card, borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, padding: 18, gap: 16 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 },
  headerPhone: { flexDirection: 'column' },
  headerCopy: { flex: 1, gap: 4 },
  title: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800' },
  subtitle: { color: reviewDesk.inkSoft, fontSize: 14, lineHeight: 20 },
  list: { gap: 8 },
  row: { minHeight: 64, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 16, borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 10 },
  rowPhone: { alignItems: 'flex-start' },
  rowMuted: { opacity: 0.6 },
  sessionCopy: { flex: 1, gap: 3 },
  sessionTitle: { color: reviewDesk.ink, fontSize: 14, fontWeight: '700' },
  sessionMeta: { color: reviewDesk.muted, fontSize: 12 },
  sessionHint: { color: reviewDesk.muted, fontSize: 12, lineHeight: 17, fontStyle: 'italic' },
  mutedInk: { color: reviewDesk.muted },
  toggleGroup: { minWidth: 170, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 10 },
  toggleGroupCompact: { minWidth: 108 },
  toggleLabel: { color: reviewDesk.inkSoft, fontSize: 12, fontWeight: '600' },
  empty: { color: reviewDesk.muted, lineHeight: 20 },
  error: { color: '#B42318', fontSize: 13, lineHeight: 18 },
});
